import React from 'react';
import { useTranslation } from 'react-i18next';
import ActivityPill from './ActivityPill';
import NotificationBell from './NotificationBell';
import ThemeToggle from './ThemeToggle';
import LangSwitch from './LangSwitch';
import { Icons as I } from '../pack-icons';
import { productTourEnabled } from '../../lib/flags';
import { CHATGPT_APP_URL } from '../../lib/connectors.js';
import { ChatGptMark } from '../../screens/OnboardingMcpShared.jsx';
import { track } from '../../lib/funnel.js';

// "?" — restarts the product tour (TASK-145). ProductTour.jsx listens for
// this event; the button only shows while the tour flag is on, so the dark
// pilot carries its own restart affordance and nobody else sees a dead "?".
function TourHelpButton() {
  const { t } = useTranslation();
  if (!productTourEnabled()) return null;
  return (
    <button
      type="button"
      className="tb-tour-help"
      aria-label={t('productTour.restart')}
      title={t('productTour.restart')}
      onClick={() => { try { window.dispatchEvent(new Event('dathent:tour:restart')); } catch (_) { /* noop */ } }}
    >
      ?
    </button>
  );
}

// "Try with ChatGPT" — always-visible topbar CTA to the approved ChatGPT app
// listing (owner request 2026-09-07). Links straight to CHATGPT_APP_URL
// (same constant Settings/OnboardingConnect use) in a new tab. Mobile shell
// fix (2026-09): on mobile (≤768px, see shell.css) it no longer collapses to
// icon-only — the founder wants ChatGPT to stay legible as a channel, so it
// shows a compact "ChatGPT" pill + external-link glyph instead. Both label
// variants are always in the DOM; CSS toggles which one paints (mirrors the
// `.tb-mobile-menu` / `.sb-close` show/hide idiom elsewhere in the shell).
// aria-label/title always carry the full `topbar.tryChatgpt` string.
function ChatGptTopbarCta() {
  const { t } = useTranslation();
  const label = t('topbar.tryChatgpt');
  const shortLabel = t('topbar.chatgptShort');
  return (
    <a
      href={CHATGPT_APP_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="btn btn-sm btn-ghost tb-chatgpt-cta"
      aria-label={label}
      title={label}
      onClick={() => { try { track('chatgpt_cta_click', { placement: 'topbar' }); } catch (_) { /* fail-silent */ } }}
    >
      <ChatGptMark size={14} />
      <span className="tb-chatgpt-cta__label">{label}</span>
      <span className="tb-chatgpt-cta__short">
        {shortLabel}
        {I.external({ size: 10 })}
      </span>
    </a>
  );
}

export default function Topbar({ title, sub, right, onMobileMenuToggle, mobileNavOpen }) {
  const { t } = useTranslation();
  return (
    <header className="topbar">
      {onMobileMenuToggle && (
        <button
          type="button"
          className="tb-mobile-menu"
          onClick={onMobileMenuToggle}
          aria-label={mobileNavOpen ? t('topbar.closeMenu') : t('topbar.openMenu')}
          aria-expanded={mobileNavOpen ? 'true' : 'false'}
        >
          {mobileNavOpen ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18M3 12h18M3 18h18" />
            </svg>
          )}
        </button>
      )}

      <div className="tb-l">
        {title && <div className="tb-title">{title}</div>}
        {sub && <div className="tb-sub mono">{sub}</div>}
      </div>

      <div className="tb-c">
        <ActivityPill />
      </div>

      <div className="tb-r">
        <ChatGptTopbarCta />
        <TourHelpButton />
        <NotificationBell />
        <ThemeToggle />
        <LangSwitch />
        {right}
      </div>
    </header>
  );
}
